import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { LuCalendar, LuTrendingUp, LuChevronLeft, LuChevronRight, LuDollarSign, LuHash, LuCircleCheck, LuClock, LuTriangleAlert } from 'react-icons/lu';
import { financeiroAPI, formatters } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PaymentsTable from '../components/PaymentsTable';
import FinancialSummary from '../components/FinancialSummary';
import ErroLoadData from '../components/ErroLoadData';

const PaymentsContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px 12px 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 18px;
  color: ${props => props.theme.colors.grayMedium};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const SummaryCard = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-direction: row;
    padding: 15px 20px;
    text-align: left;
    align-items: center;
  }
`;

const CardIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  background: #fef2f2;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-bottom: 0;
    margin-right: 15px;
    width: 48px;
    height: 48px;
    font-size: 24px;
  }
`;

const SummaryCardText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 2px;
  }
`;

const CardLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const FiltersSection = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;


const FiltersTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 16px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 150px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: ${props => props.theme.colors.white};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const NavButton = styled.button`
  background: white;
  color: ${props => props.theme.colors.primary};
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  margin-bottom: 24px;
  text-align: center;
`;

const NoDataMessage = styled.div`
  background: ${props => props.theme.colors.lightBlue};
  color: ${props => props.theme.colors.primary};
  padding: 32px;
  border-radius: 12px;
  text-align: center;
  font-size: 16px;
`;

const YearSection = styled.div`
  margin-bottom: 40px;
`;

const YearHeader = styled.div`
  background: white;
  color: ${props => props.theme.colors.primary};
  padding: 24px;
  border-radius: 16px 16px 0 0;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.02);
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f1f5f9;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  @media (max-width: 992px) {
    margin-bottom: 8px;
  }
`;

const YearTitle = styled.h2`
  font-size: 20px;
  font-weight: 800;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  min-width: 60px;
  text-align: center;
  user-select: none;
`;

const NavIconButton = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  color: ${props => props.theme.colors.primary};
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const YearStats = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const StatChip = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${props => props.bgColor || '#f1f5f9'};
  color: ${props => props.color || '#475569'};
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${props => props.borderColor || '#e2e8f0'};

  svg {
    opacity: 0.8;
  }
`;

const Payments = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [groupedPayments, setGroupedPayments] = useState({});
  const [currentYear, setCurrentYear] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    totalValue: 0,
    paidValue: 0,
    pendingValue: 0,
    overdueValue: 0
  });

  // Função para agrupar pagamentos por ano
  const groupPaymentsByYear = (paymentsData) => {
    const grouped = {};

    paymentsData.forEach(payment => {
      const year = payment.mesAno.split('/')[1];
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(payment);
    });

    // Ordenar pagamentos dentro de cada ano por mês
    Object.keys(grouped).forEach(year => {
      const monthOrder = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];

      grouped[year].sort((a, b) => {
        const monthA = a.mesAno.split('/')[0];
        const monthB = b.mesAno.split('/')[0];
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
    });

    return grouped;
  };

  // Carregar dados de pagamentos
  useEffect(() => {
    const loadPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await financeiroAPI.getPayments();

        if (response.data.success) {
          const paymentsData = response.data.data;

          // Transformar dados para o formato esperado
          const transformedPayments = paymentsData.map(payment => {

            // Determinar o status correto baseado nas datas
            const statusCorreto = payment.status;

            return {
              seq: payment.seq,
              idBoleto: payment.idBoleto,
              mesAno: payment.mesAno,
              vencimento: formatters.formatDate(payment.vencimento),
              pagamento: payment.pagamento ? formatters.formatDate(payment.pagamento) : '-',
              valor: payment.valor,
              status: statusCorreto,
              linhaDigitavel: payment.linhaDigitavel,
              codigoBarras: payment.codigoBarras,
              pixCopiaECola: payment.pixCopiaECola,
              pixTXID: payment.pixTXID,
              statusPix: payment.statusPix,
              originalData: payment,
              isOverdue: payment.status
            };
          });

          setPayments(transformedPayments);

          // Agrupar por ano
          const grouped = groupPaymentsByYear(transformedPayments);
          setGroupedPayments(grouped);

          // Definir ano atual como ponto de partida
          const currentYear = new Date().getFullYear().toString();
          const years = Object.keys(grouped).sort((a, b) => b - a);

          // Sempre usar o ano atual como foco inicial, mesmo que não haja dados
          // Se não houver dados para o ano atual, usar o ano mais recente disponível
          const yearToSet = grouped[currentYear] ? currentYear : (years[0] || currentYear);
          setCurrentYear(yearToSet);

          // Calcular resumo
          calculateSummary(transformedPayments);
        } else {
          throw new Error('Erro ao carregar pagamentos');
        }
      } catch (err) {
        console.error('Erro ao carregar pagamentos:', err);
        setError(err.message || 'Erro ao carregar dados de pagamentos');
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, []);

  // Calcular resumo dos pagamentos
  const calculateSummary = (paymentsData) => {
    const total = paymentsData.length;
    const paid = paymentsData.filter(p => p.status === 'PAGO').length;
    const aVencer = paymentsData.filter(p => p.status === 'ABERTO').length;
    const venceHoje = paymentsData.filter(p => p.status === 'VENCE HOJE').length;
    
    // Só considera como vencidos, boletos que tenham menos de um ano que não foram pagos.    
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    const isRecentOverdue = (payment) => {
      const dataFormatada = payment.vencimento.split('/')[2] + '-' + payment.vencimento.split('/')[1] + '-' + payment.vencimento.split('/')[0]; 
      const vencimentoDate = new Date(dataFormatada);
      return vencimentoDate && vencimentoDate > sixMonthsAgo;
    };

    const pagamentosVencidos = paymentsData.filter(p => 
      p.status === 'VENCIDO' && isRecentOverdue(p)
    );

    const emAtraso = pagamentosVencidos.length;
    const overdue = pagamentosVencidos.length;

    const totalValue = paymentsData.reduce((sum, p) => sum + p.valor, 0);
    const paidValue = paymentsData
      .filter(p => p.status === 'PAGO')
      .reduce((sum, p) => sum + p.valor, 0);
    const pendingValue = paymentsData
      .filter(p => p.status !== 'ABERTO')
      .reduce((sum, p) => sum + p.valor, 0);
    const overdueValue = pagamentosVencidos
      .filter(p => p.status === 'VENCIDO')
      .reduce((sum, p) => sum + p.valor, 0);

    setSummary({
      total,
      paid,
      pending: aVencer + venceHoje + emAtraso,
      overdue,
      totalValue,
      paidValue,
      pendingValue,
      overdueValue
    });
  };

  // Obter anos únicos dos pagamentos
  const getUniqueYears = () => {
    const years = Object.keys(groupedPayments).sort((a, b) => b - a);
    const currentYear = new Date().getFullYear().toString();

    // Incluir o ano atual mesmo que não haja dados
    if (!years.includes(currentYear)) {
      years.unshift(currentYear);
    }

    return years;
  };

  // Verificar se existe próximo ano com dados
  const hasNextYear = () => {
    if (!currentYear) return false;
    const nextYear = (parseInt(currentYear) + 1).toString();
    return groupedPayments[nextYear] && groupedPayments[nextYear].length > 0;
  };

  // Verificar se existe ano anterior com dados
  const hasPrevYear = () => {
    if (!currentYear) return false;
    const prevYear = (parseInt(currentYear) - 1).toString();
    return groupedPayments[prevYear] && groupedPayments[prevYear].length > 0;
  };

  // Obter meses únicos dos pagamentos
  const getUniqueMonths = () => {
    const months = [...new Set(payments.map(p => p.mesAno.split('/')[0]))];
    const monthOrder = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
  };

  // Navegar entre anos de forma dinâmica
  const goToNextYear = () => {
    if (currentYear) {
      const nextYear = (parseInt(currentYear) + 1).toString();
      // Verificar se existem dados para o próximo ano
      if (groupedPayments[nextYear] && groupedPayments[nextYear].length > 0) {
        setCurrentYear(nextYear);
      }
    }
  };

  const goToPrevYear = () => {
    if (currentYear) {
      const prevYear = (parseInt(currentYear) - 1).toString();
      // Verificar se existem dados para o ano anterior
      if (groupedPayments[prevYear] && groupedPayments[prevYear].length > 0) {
        setCurrentYear(prevYear);
      }
    }
  };

  // Obter pagamentos do ano atual
  const getCurrentYearPayments = () => {
    if (!currentYear || !groupedPayments[currentYear]) return [];
    return groupedPayments[currentYear];
  };

  // Calcular estatísticas do ano atual
  const getCurrentYearStats = () => {
    const yearPayments = getCurrentYearPayments();
    const paid = yearPayments.filter(p => p.status === 'PAGO').length;
    const aVencer = yearPayments.filter(p => p.status === 'ABERTO').length;
    const venceHoje = yearPayments.filter(p => p.status === 'VENCE HOJE').length;
    const emAtraso = yearPayments.filter(p => p.status === 'VENCIDO').length;
    const overdue = yearPayments.filter(p => p.status === 'VENCIDO').length;

    return {
      paid,
      pending: aVencer + venceHoje + emAtraso,
      overdue,
      total: yearPayments.length
    };
  };

  if (loading) {
    return (
      <PaymentsContainer>
        <LoadingSpinner />
      </PaymentsContainer>
    );
  }

  const years = getUniqueYears();
  const currentYearStats = getCurrentYearStats();

  if (error) {
    return (
      <PaymentsContainer>
        <ErroLoadData></ErroLoadData>
      </PaymentsContainer>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PaymentsContainer>
      <PageHeader>
        <PageTitle>Histórico de Pagamentos</PageTitle>
        <PageSubtitle>Gerencie e acompanhe seus pagamentos</PageSubtitle>
      </PageHeader>

      {summary.overdue > 0 && (
        <SummaryCards>
          <SummaryCard>
            <CardIcon style={{ color: '#ef4444' }}>
              <LuTriangleAlert />
            </CardIcon>
            <SummaryCardText>
              <CardValue style={{ color: '#ef4444' }}>
                {summary.overdue || 0}
              </CardValue>
              <CardLabel>Pagamentos em Atraso</CardLabel>
            </SummaryCardText>
          </SummaryCard>

          <SummaryCard>
            <CardIcon style={{ color: '#ef4444' }}>
              <LuTriangleAlert />
            </CardIcon>
            <SummaryCardText>
              <CardValue style={{ color: '#ef4444' }}>
                {formatters.formatCurrency(summary.overdueValue || 0)}
              </CardValue>
              <CardLabel>Valor Total em Atraso</CardLabel>
            </SummaryCardText>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Resumo Financeiro Detalhado */}
      <FinancialSummary summary={summary} payments={payments} />


      {/* Tabela de Pagamentos por Ano */}
      {currentYear && (
        <YearSection>
          <YearHeader>
            <YearNav>
              <NavIconButton onClick={goToPrevYear} disabled={!hasPrevYear()} title="Ano Anterior">
                <LuChevronLeft size={20} />
              </NavIconButton>
              <YearTitle>{currentYear}</YearTitle>
              <NavIconButton onClick={goToNextYear} disabled={!hasNextYear()} title="Próximo Ano">
                <LuChevronRight size={20} />
              </NavIconButton>
            </YearNav>

            {groupedPayments[currentYear] && groupedPayments[currentYear].length > 0 && (
              <YearStats>
                <StatChip bgColor="#f8fafc" color="#64748b" borderColor="#e2e8f0">
                  <LuHash size={14} /> {currentYearStats.total} total
                </StatChip>
                <StatChip bgColor="#f0fdf4" color="#166534" borderColor="#bbf7d0">
                  <LuCircleCheck size={14} /> {currentYearStats.paid} pagos
                </StatChip>
                <StatChip bgColor="#fffbeb" color="#92400e" borderColor="#fef3c7">
                  <LuClock size={14} /> {currentYearStats.pending} a vencer
                </StatChip>
                {currentYearStats.overdue > 0 && (
                  <StatChip bgColor="#fef2f2" color="#991b1b" borderColor="#fecaca">
                    <LuTriangleAlert size={14} /> {currentYearStats.overdue} em atraso
                  </StatChip>
                )}
              </YearStats>
            )}
          </YearHeader>
          {groupedPayments[currentYear] && groupedPayments[currentYear].length > 0 ? (
            <PaymentsTable
              pagamentos={groupedPayments[currentYear]}
              allPagamentos={payments}
              showOverdueIcon={true}
            />
          ) : (
            <NoDataMessage>
              <p>Nenhum pagamento encontrado para o ano {currentYear}.</p>
              <p>Use os botões de navegação para ver pagamentos de outros anos.</p>
            </NoDataMessage>
          )}
        </YearSection>
      )}
      </PaymentsContainer>
    </motion.div>
  );
};

export default Payments;
